import { FormEvent, useEffect, useRef, useState } from "react"
import Button from "../../button/button"
import EditorOverlay from "./EditorOverlay"

type LinkInsertModalProps = {
  isOpen: boolean
  initialUrl?: string
  initialText?: string
  isEditing?: boolean
  requireText?: boolean
  onClose: () => void
  onSave: (url: string, text: string) => void
  onRemove?: () => void
}

const LinkInsertModal = ({
  isOpen,
  initialUrl = "",
  initialText = "",
  isEditing = false,
  requireText = false,
  onClose,
  onSave,
  onRemove,
}: LinkInsertModalProps) => {
  const textInputRef = useRef<HTMLInputElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)
  const [url, setUrl] = useState(initialUrl)
  const [text, setText] = useState(initialText)
  const [urlError, setUrlError] = useState("")
  const [textError, setTextError] = useState("")

  const showTextField = requireText || isEditing

  useEffect(() => {
    if (!isOpen) return

    setUrl(initialUrl)
    setText(initialText)
    setUrlError("")
    setTextError("")

    const focusTimer = window.setTimeout(() => {
      const focusTarget = showTextField ? textInputRef.current : urlInputRef.current
      focusTarget?.focus()
      focusTarget?.select()
    }, 0)

    return () => window.clearTimeout(focusTimer)
  }, [isOpen, initialUrl, initialText, showTextField])

  const handleSave = () => {
    const trimmedUrl = url.trim()
    const trimmedText = text.trim()
    let hasError = false

    if (!trimmedUrl) {
      setUrlError("Enter a URL")
      hasError = true
    }

    if (requireText && !trimmedText) {
      setTextError("Enter link text")
      hasError = true
    }

    if (hasError) return

    onSave(trimmedUrl, trimmedText)
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    handleSave()
  }

  const modalDescription = isEditing
    ? "Update the display text or web address for this link."
    : requireText
      ? "Enter the text to show and the web address it should link to."
      : "Add a web address for the selected text."

  return (
    <EditorOverlay
      isOpen={isOpen}
      onClose={onClose}
      className='tiptap-editor__link-modal'
    >
      <div className='tiptap-editor__link-modal-header'>
        <div>
          <h3>{isEditing ? "Edit link" : "Insert link"}</h3>
          <p>{modalDescription}</p>
        </div>

        <div className='tiptap-editor__link-modal-actions'>
          {isEditing && onRemove && (
            <Button type='secondary' text='Remove link' onClick={onRemove} />
          )}
          <Button type='secondary' text='Cancel' onClick={onClose} />
          <Button text={isEditing ? "Save" : "Insert"} onClick={handleSave} />
        </div>
      </div>

      <form className='tiptap-editor__link-form' onSubmit={handleSubmit}>
        {showTextField && (
          <label className='tiptap-editor__link-field' htmlFor='tiptap-link-text'>
            <span>Text{requireText ? " *" : ""}</span>
            <input
              ref={textInputRef}
              id='tiptap-link-text'
              type='text'
              value={text}
              placeholder='Link text'
              onChange={(event) => {
                setText(event.target.value)
                if (textError) setTextError("")
              }}
            />
          </label>
        )}
        <label className='tiptap-editor__link-field' htmlFor='tiptap-link-url'>
          <span>URL *</span>
          <input
            ref={urlInputRef}
            id='tiptap-link-url'
            type='url'
            value={url}
            placeholder='https://example.com'
            onChange={(event) => {
              setUrl(event.target.value)
              if (urlError) setUrlError("")
            }}
          />
        </label>
        {textError && <p className='tiptap-editor__link-error'>{textError}</p>}
        {urlError && <p className='tiptap-editor__link-error'>{urlError}</p>}
      </form>
    </EditorOverlay>
  )
}

export default LinkInsertModal
